import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart3,
  TrendingUp,
  Users,
  DoorOpen,
  Calendar,
  IndianRupee,
  RefreshCw
} from "lucide-react";

export default function HotelReports() {
  const [occupancyData, setOccupancyData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [arrivalsData, setArrivalsData] = useState([]);
  const [departuresData, setDeparturesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllReports();
  }, []);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const [occupancy, revenue, arrivals, departures] = await Promise.all([
        api.get("/hotel/reports/occupancy"),
        api.get("/hotel/reports/revenue"),
        api.get("/hotel/reports/arrivals"),
        api.get("/hotel/reports/departures")
      ]);

      setOccupancyData(occupancy.data.data);
      setRevenueData(revenue.data.data);
      setArrivalsData(arrivals.data.data);
      setDeparturesData(departures.data.data);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-full">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#2D2C2A]">
            Hotel Reports
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Real-time insights and analytics
          </p>
        </div>
        <Button onClick={fetchAllReports} variant="outline" className="gap-2">
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Occupancy Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <DoorOpen size={20} className="text-[#D4AF37]" />
            <div className="text-xs text-[#7A7873] uppercase">Total Rooms</div>
          </div>
          <div className="text-3xl font-bold text-[#2D2C2A]">
            {occupancyData?.total_rooms || 0}
          </div>
        </Card>

        <Card className="p-4 bg-green-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <div className="text-xs text-[#7A7873] uppercase">Available</div>
          </div>
          <div className="text-3xl font-bold text-green-700">
            {occupancyData?.available || 0}
          </div>
        </Card>

        <Card className="p-4 bg-red-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="text-xs text-[#7A7873] uppercase">Occupied</div>
          </div>
          <div className="text-3xl font-bold text-red-700">
            {occupancyData?.occupied || 0}
          </div>
        </Card>

        <Card className="p-4 bg-yellow-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="text-xs text-[#7A7873] uppercase">Dirty</div>
          </div>
          <div className="text-3xl font-bold text-yellow-700">
            {occupancyData?.dirty || 0}
          </div>
        </Card>

        <Card className="p-4 bg-blue-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <div className="text-xs text-[#7A7873] uppercase">Reserved</div>
          </div>
          <div className="text-3xl font-bold text-blue-700">
            {occupancyData?.reserved || 0}
          </div>
        </Card>

        <Card className="p-4 bg-[#D4AF37] bg-opacity-10">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={20} className="text-[#D4AF37]" />
            <div className="text-xs text-[#7A7873] uppercase">Occupancy</div>
          </div>
          <div className="text-3xl font-bold text-[#D4AF37]">
            {occupancyData?.occupancy_rate?.toFixed(1) || 0}%
          </div>
        </Card>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <IndianRupee size={24} className="text-green-600" />
            <h2 className="font-semibold text-lg">Revenue Overview</h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <div>
                <div className="text-sm text-[#7A7873]">Today's Revenue</div>
                <div className="text-2xl font-bold text-green-700">
                  ₹{parseFloat(revenueData?.today || 0).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <div>
                <div className="text-sm text-[#7A7873]">This Month</div>
                <div className="text-2xl font-bold text-blue-700">
                  ₹{parseFloat(revenueData?.month || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={24} className="text-[#D4AF37]" />
            <h2 className="font-semibold text-lg">Quick Stats</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-sm text-[#7A7873]">Total Rooms</span>
              <span className="font-bold">{occupancyData?.total_rooms || 0}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-sm text-[#7A7873]">Checked-In Today</span>
              <span className="font-bold">{arrivalsData?.length || 0}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-sm text-[#7A7873]">Check-Out Due</span>
              <span className="font-bold">{departuresData?.length || 0}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Arrivals & Departures */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Arrivals */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={20} className="text-green-600" />
            <h2 className="font-semibold text-lg">Today's Arrivals ({arrivalsData?.length || 0})</h2>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {arrivalsData && arrivalsData.length > 0 ? (
              arrivalsData.map((arrival) => (
                <div key={arrival.id} className="p-3 bg-green-50 rounded flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{arrival.guest_name}</div>
                    <div className="text-sm text-[#7A7873]">
                      Room {arrival.room_number} • {arrival.booking_code}
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded text-xs font-medium ${
                    arrival.status === 'Checked-In' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-blue-500 text-white'
                  }`}>
                    {arrival.status}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-[#7A7873] py-8">
                No arrivals today
              </div>
            )}
          </div>
        </Card>

        {/* Today's Departures */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={20} className="text-red-600" />
            <h2 className="font-semibold text-lg">Today's Departures ({departuresData?.length || 0})</h2>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {departuresData && departuresData.length > 0 ? (
              departuresData.map((departure) => (
                <div key={departure.id} className="p-3 bg-red-50 rounded flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{departure.guest_name}</div>
                    <div className="text-sm text-[#7A7873]">
                      Room {departure.room_number} • {departure.booking_code}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-red-500 hover:bg-red-600"
                    onClick={() => window.location.href = `/hotel-checkout/${departure.id}`}
                  >
                    Check-Out
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center text-[#7A7873] py-8">
                No departures due today
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
